import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  IconButton, TextField, InputAdornment, Grid, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, Toolbar, Stack, Divider, Tabs, Tab,
  Drawer, Tooltip
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Add as AddIcon, DeleteSweep as DeleteSweepIcon,
  Timeline as TimelineIcon, InsertDriveFile as FileIcon,
  FilterAltOff as ResetFilterIcon
} from '@mui/icons-material';
import { contentService } from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import { useAuth } from '../context/AuthContext';
import ContentRoadmap from '../components/ContentRoadmap';
import { openScriptSecure } from '../utils/fileUrl';

function ContentList() {
  const location = useLocation();

  // Restore filters from sessionStorage so they survive navigation to Edit and back
  const savedFilters = (() => {
    try { return JSON.parse(sessionStorage.getItem('contentListFilters') || '{}'); } catch { return {}; }
  })();

  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]                         = useState(savedFilters.search                 || '');
  const [statusFilter, setStatusFilter]             = useState(savedFilters.statusFilter           || []);
  const [channelFilter, setChannelFilter]           = useState(savedFilters.channelFilter          || []);
  const [typeFilter, setTypeFilter]                 = useState(savedFilters.typeFilter             || []);
  const [directorFilter, setDirectorFilter]         = useState(savedFilters.directorFilter         || []);
  const [editorFilter, setEditorFilter]             = useState(savedFilters.editorFilter           || []);
  const [cinematographerFilter, setCinematographerFilter] = useState(savedFilters.cinematographerFilter || []);
  const [castingFilter, setCastingFilter]           = useState(savedFilters.castingFilter          || []);
  const [sortBy, setSortBy]                         = useState(savedFilters.sortBy                 || 'shootDate');
  const [sortOrder, setSortOrder]                   = useState(savedFilters.sortOrder              || 'desc');
  const [staleDays, setStaleDays]                   = useState(savedFilters.staleDays              || '');
  const [selectedTab, setSelectedTab]               = useState(savedFilters.selectedTab            ?? 2);
  const [selectedWeek, setSelectedWeek]             = useState(savedFilters.selectedWeek           ?? 0);
  const [selectedMonth, setSelectedMonth]           = useState(savedFilters.selectedMonth          || getCurrentMonth());
  const [deleteDialog, setDeleteDialog]     = useState({ open: false, id: null });
  const [selectedItems, setSelectedItems]   = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [dropdowns, setDropdowns]           = useState({});
  const [roadmapDrawer, setRoadmapDrawer]   = useState({ open: false, item: null });
  const navigate = useNavigate();
  const { user } = useAuth();

  // Persist filters to sessionStorage whenever any filter changes
  useEffect(() => {
    sessionStorage.setItem('contentListFilters', JSON.stringify({
      search, statusFilter, channelFilter, typeFilter,
      directorFilter, editorFilter, cinematographerFilter, castingFilter,
      sortBy, sortOrder, staleDays, selectedTab, selectedWeek, selectedMonth
    }));
  }, [search, statusFilter, channelFilter, typeFilter, directorFilter, editorFilter,
      cinematographerFilter, castingFilter, sortBy, sortOrder, staleDays,
      selectedTab, selectedWeek, selectedMonth]);
  
  // Check permissions
  const canDelete = ['owner', 'manager', 'hr'].includes(user?.role);
  const canSelectAll = user?.role === 'owner';

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  function generateWeekOptions() {
    const options = [];
    for (let i = 0; i < 4; i++) {
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - (i * 7));
      const day = weekStart.getDay();
      const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
      weekStart.setDate(diff);
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      options.push({
        value: i,
        label: i === 0 ? 'This Week' : `${i} ${i === 1 ? 'Week' : 'Weeks'} Ago`,
        displayLabel: `${weekStart.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - ${weekEnd.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
      });
    }
    return options;
  }

  function generateMonthOptions() {
    const options = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label, isCurrent: i === 0 });
    }
    
    return options;
  }

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, channelFilter, typeFilter, selectedTab, selectedWeek, selectedMonth, staleDays]);

  const fetchDropdowns = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1'}/dropdowns`);
      const data = await response.json();
      if (data.success) {
        setDropdowns(data.data);
      }
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  // Get filtered and sorted content
  const getFilteredAndSortedContent = () => {
    let filtered = [...content];

    // Apply status filter (client-side OR)
    if (statusFilter.length > 0) {
      filtered = filtered.filter(item => statusFilter.includes(item.status));
    }

    // Apply channel filter (client-side OR)
    if (channelFilter.length > 0) {
      filtered = filtered.filter(item => channelFilter.includes(item.channel));
    }

    // Apply type filter
    if (typeFilter.length > 0) {
      filtered = filtered.filter(item => typeFilter.includes(item.contentType));
    }

    // Apply director filter
    if (directorFilter.length > 0) {
      filtered = filtered.filter(item => directorFilter.includes(item.directorId));
    }

    // Apply editor filter
    if (editorFilter.length > 0) {
      filtered = filtered.filter(item => editorFilter.includes(item.editorId));
    }

    // Apply cinematographer filter
    if (cinematographerFilter.length > 0) {
      filtered = filtered.filter(item => cinematographerFilter.includes(item.cinematographerId));
    }

    // Apply casting filter (check if any selected actor is in castingIds array)
    if (castingFilter.length > 0) {
      filtered = filtered.filter(item => {
        let castingIds = item.castingIds || [];
        if (typeof castingIds === 'string') {
          try { castingIds = JSON.parse(castingIds); } catch (e) { castingIds = []; }
        }
        return Array.isArray(castingIds) && castingFilter.some(f => castingIds.includes(f));
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = sortBy === 'shootDate' ? new Date(a.shootDate || 0) : new Date(a.releaseDate || 0);
      const dateB = sortBy === 'shootDate' ? new Date(b.shootDate || 0) : new Date(b.releaseDate || 0);
      
      if (sortOrder === 'asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    
    return filtered;
  };

  const sortedContent = getFilteredAndSortedContent();

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = {
        search,
        limit: 1000
      };

      if (staleDays) {
        params.staleDays = staleDays;
      } else if (selectedTab === 0) {
        // Weekly
        params.weekOffset = selectedWeek;
      } else if (selectedTab === 1) {
        // Monthly
        const [year, month] = selectedMonth.split('-');
        params.year = year;
        params.month = month;
      } else if (selectedTab === 2) {
        // Last 30 Days
        params.last30Days = true;
      }

      const response = await contentService.getAll(params);
      setContent(response.data.data.content || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await contentService.delete(deleteDialog.id);
      setDeleteDialog({ open: false, id: null });
      fetchContent();
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedItems(content.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedItems.map(id => contentService.delete(id)));
      setBulkDeleteDialog(false);
      setSelectedItems([]);
      fetchContent();
    } catch (error) {
      console.error('Error bulk deleting content:', error);
    }
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter([]);
    setChannelFilter([]);
    setTypeFilter([]);
    setDirectorFilter([]);
    setEditorFilter([]);
    setCinematographerFilter([]);
    setCastingFilter([]);
    setStaleDays('');
    sessionStorage.removeItem('contentListFilters');
  };

  const hasActiveFilters = search || statusFilter.length > 0 || channelFilter.length > 0 ||
    typeFilter.length > 0 || directorFilter.length > 0 || editorFilter.length > 0 ||
    cinematographerFilter.length > 0 || castingFilter.length > 0 || staleDays;

  const handleExport = () => {
    try {
      const params = new URLSearchParams({
        search,
        status: statusFilter.length > 0 ? statusFilter.join(',') : '',
        channel: channelFilter.length > 0 ? channelFilter.join(',') : ''
      });

      // Add date filtering based on selected tab
      if (selectedTab === 0) {
        params.append('weekOffset', selectedWeek);
      } else if (selectedTab === 1) {
        const [year, month] = selectedMonth.split('-');
        params.append('year', year);
        params.append('month', month);
      } else if (selectedTab === 2) {
        params.append('last30Days', 'true');
      }

      // Use the API base URL from the service
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
      const url = `${baseURL}/content/export?${params.toString()}`;
      
      console.log('Export URL:', url);
      
      // Open in new window to trigger download
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error exporting content:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Planning Completed': 'default',
      'Content Approved': 'warning',
      'Content Ready': 'info',
      'Sent for Revision': 'error',
      'Shoot Completed': 'warning',
      'Editing Completed': 'secondary',
      'Released': 'success'
    };
    return colors[status] || 'default';
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
              Content Library
            </Typography>
            {!loading && (
              <Chip 
                label={`Total: ${sortedContent.length} items`} 
                color="primary" 
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          {selectedItems.length > 0 && (
            <Typography variant="body2" color="primary" mt={1}>
              {selectedItems.length} item(s) selected
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {selectedItems.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setBulkDeleteDialog(true)}
              size="small"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Delete Selected ({selectedItems.length})
            </Button>
          )}
          {selectedItems.length > 0 && (
            <IconButton
              color="error"
              onClick={() => setBulkDeleteDialog(true)}
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <DeleteSweepIcon />
            </IconButton>
          )}
          <Button
            variant="outlined"
            onClick={handleExport}
            size="small"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            📥 Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/content/new')}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Content
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Add
            </Box>
          </Button>
        </Box>
      </Box>

      {/* Time Period Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
            <Tabs 
              value={selectedTab} 
              onChange={(e, newValue) => {
                setSelectedTab(newValue);
                if (newValue === 0) setSelectedWeek(0);
                if (newValue === 1) setSelectedMonth(getCurrentMonth());
              }}
              sx={{ borderBottom: 1, borderColor: 'divider', flexGrow: 1 }}
            >
              <Tab label="📅 Weekly" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
              <Tab label="📆 Monthly" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
              <Tab label="📋 Last 30 Days" sx={{ textTransform: 'none', fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            </Tabs>
            
            {selectedTab === 0 && (
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>Select Week</InputLabel>
                <Select
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  label="Select Week"
                >
                  {generateWeekOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} ({option.displayLabel})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {selectedTab === 1 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Select Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Select Month"
                >
                  {generateMonthOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label} {option.isCurrent && '(Current)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search content..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  multiple
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value="Content Ready">Content Ready</MenuItem>
                  <MenuItem value="Sent for Revision">Sent for Revision</MenuItem>
                  <MenuItem value="Content Approved">Content Approved</MenuItem>
                  <MenuItem value="Planning Completed">Planning Completed</MenuItem>
                  <MenuItem value="Shoot Completed">Shoot Completed</MenuItem>
                  <MenuItem value="Editing Completed">Editing Completed</MenuItem>
                  <MenuItem value="Released">Released</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  multiple
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value="Long Video">Long Video</MenuItem>
                  <MenuItem value="Shorts">Shorts</MenuItem>
                  <MenuItem value="Ad Content">Ad Content</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  multiple
                  value={channelFilter}
                  label="Channel"
                  onChange={(e) => setChannelFilter(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {(dropdowns.channels || []).map((ch) => (
                    <MenuItem key={ch} value={ch}>{ch}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="shootDate">Shoot Date</MenuItem>
                  <MenuItem value="releaseDate">Release Date</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Pending Since</InputLabel>
                <Select
                  value={staleDays}
                  label="Pending Since"
                  onChange={(e) => { setStaleDays(e.target.value); }}
                >
                  <MenuItem value="">All Content</MenuItem>
                  <MenuItem value="30">Stuck &gt; 30 days</MenuItem>
                  <MenuItem value="45">Stuck &gt; 45 days</MenuItem>
                  <MenuItem value="60">Stuck &gt; 60 days</MenuItem>
                  <MenuItem value="90">Stuck &gt; 90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Second Row - Team Filters */}
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Director</InputLabel>
                <Select
                  multiple
                  value={directorFilter}
                  label="Director"
                  onChange={(e) => setDirectorFilter(e.target.value)}
                  renderValue={(selected) => selected.map(v => (dropdowns.director || []).find(d => d.username === v)?.name || v).join(', ')}
                >
                  {(dropdowns.director || []).map((dir) => (
                    <MenuItem key={dir.username} value={dir.username}>
                      {dir.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Editor</InputLabel>
                <Select
                  multiple
                  value={editorFilter}
                  label="Editor"
                  onChange={(e) => setEditorFilter(e.target.value)}
                  renderValue={(selected) => selected.map(v => (dropdowns.editor || []).find(d => d.username === v)?.name || v).join(', ')}
                >
                  {(dropdowns.editor || []).map((ed) => (
                    <MenuItem key={ed.username} value={ed.username}>
                      {ed.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Cinematographer</InputLabel>
                <Select
                  multiple
                  value={cinematographerFilter}
                  label="Cinematographer"
                  onChange={(e) => setCinematographerFilter(e.target.value)}
                  renderValue={(selected) => selected.map(v => (dropdowns.cinematographer || []).find(d => d.username === v)?.name || v).join(', ')}
                >
                  {(dropdowns.cinematographer || []).map((cine) => (
                    <MenuItem key={cine.username} value={cine.username}>
                      {cine.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Casting</InputLabel>
                <Select
                  multiple
                  value={castingFilter}
                  label="Casting"
                  onChange={(e) => setCastingFilter(e.target.value)}
                  renderValue={(selected) => selected.map(v => (dropdowns.actor || []).find(d => d.username === v)?.name || v).join(', ')}
                >
                  {(dropdowns.actor || []).map((actor) => (
                    <MenuItem key={actor.username} value={actor.username}>
                      {actor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {hasActiveFilters && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<ResetFilterIcon />}
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Desktop Table View */}
          <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {canSelectAll && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.length === sortedContent.length && sortedContent.length > 0}
                      indeterminate={selectedItems.length > 0 && selectedItems.length < sortedContent.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell><strong>Content ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Channel</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Director</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Shoot Date</strong></TableCell>
                <TableCell><strong>Release Date</strong></TableCell>
                <TableCell align="center"><strong>Edit Hrs</strong></TableCell>
                <TableCell align="center"><strong>Dir Hrs</strong></TableCell>
                <TableCell align="center"><strong>Script</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContent.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <Typography color="text.secondary" py={3}>
                      No content found. Click "Add Content" to create your first entry.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedContent.map((item) => (
                  <TableRow 
                    key={item.id} 
                    hover
                    selected={selectedItems.includes(item.id)}
                    onClick={() => navigate(`/content/edit/${item.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {canSelectAll && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {item.contentId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.contentTitle}</TableCell>
                    <TableCell>{item.channel}</TableCell>
                    <TableCell>{item.contentType}</TableCell>
                    <TableCell>{item.director || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.shootDate) || '-'}</TableCell>
                    <TableCell>
                      {item.releaseDate ? (
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {formatDate(item.releaseDate)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {item.totalEditingEffort != null
                        ? <Chip label={`${item.totalEditingEffort} hrs`} size="small" sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 600 }} />
                        : <Typography variant="body2" color="text.secondary">-</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      {item.totalDirectingEffort != null
                        ? <Chip label={`${item.totalDirectingEffort} hrs`} size="small" sx={{ bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 600 }} />
                        : <Typography variant="body2" color="text.secondary">-</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      {item.scriptFile ? (
                        <Tooltip title={`Script: ${(typeof item.scriptFile === 'string' ? JSON.parse(item.scriptFile) : item.scriptFile)?.originalName || 'View'}`} arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              const sf = typeof item.scriptFile === 'string' ? JSON.parse(item.scriptFile) : item.scriptFile;
                              openScriptSecure(item.id);
                            }}
                          >
                            <FileIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        color="info"
                        title="View Progress Roadmap"
                        onClick={() => setRoadmapDrawer({ open: true, item })}
                      >
                        <TimelineIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/content/edit/${item.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, id: item.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {sortedContent.length === 0 ? (
              <Card>
                <CardContent>
                  <Typography color="text.secondary" align="center">
                    No content found. Click "Add Content" to create your first entry.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {sortedContent.map((item) => (
                  <Card 
                    key={item.id} 
                    variant="outlined"
                    onClick={() => navigate(`/content/edit/${item.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box flex={1}>
                          <Typography variant="caption" color="primary" fontWeight="bold">
                            {item.contentId || 'No ID'}
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {item.contentTitle}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setRoadmapDrawer({ open: true, item })}
                          >
                            <TimelineIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/content/edit/${item.id}`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {canDelete && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, id: item.id })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      
                      <Stack spacing={1}>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Chip label={item.channel} size="small" color="primary" variant="outlined" />
                          <Chip label={item.contentType} size="small" color="secondary" variant="outlined" />
                        </Box>
                        
                        <Divider />
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Director:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {item.director || '-'}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Shoot Date:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(item.shootDate) || '-'}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Release Date:
                          </Typography>
                          {item.releaseDate ? (
                            <Typography variant="body2" color="success.main" fontWeight={600}>
                              {formatDate(item.releaseDate)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          <Chip
                            label={item.status}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </Box>

                        {item.scriptFile && (
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              Script:
                            </Typography>
                            <Tooltip title={`Script: ${(typeof item.scriptFile === 'string' ? JSON.parse(item.scriptFile) : item.scriptFile)?.originalName || 'View Script'}`} arrow>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => { e.stopPropagation(); openScriptSecure(item.id); }}
                              >
                                <FileIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </>
      )}

      {/* Single Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this content? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)}>
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedItems.length} selected item(s)? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Progress Roadmap Drawer */}
      <Drawer
        anchor="bottom"
        open={roadmapDrawer.open}
        onClose={() => setRoadmapDrawer({ open: false, item: null })}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 3, maxHeight: '50vh' } }}
      >
        {roadmapDrawer.item && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="subtitle2" color="primary" fontWeight={700}>
                  {roadmapDrawer.item.contentId}
                </Typography>
                <Typography variant="h6" fontWeight={600}>{roadmapDrawer.item.contentTitle}</Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => { setRoadmapDrawer({ open: false, item: null }); navigate(`/content/edit/${roadmapDrawer.item.id}`); }}
              >
                Edit
              </Button>
            </Box>
            <ContentRoadmap content={roadmapDrawer.item} compact />
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

export default ContentList;
